// components/Media.jsx
import axios from "axios";
import React, { useEffect, useState } from "react";

// Media type enum
const MediaType = {
  Image: "Image",
  Video: "Video",
};

const Media = () => {
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingMedia, setEditingMedia] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    type: MediaType.Image,
    file: null,
  });
  const [filePreview, setFilePreview] = useState(null);

  const [mediaItems, setMediaItems] = useState([
    {
      id: 1,
      title: "Nature Landscape",
      type: MediaType.Image,
      file: "/api/placeholder/400/300",
      createdAt: "2024-01-15",
      size: "2.4 MB",
    },
    {
      id: 2,
      title: "Product Demo Video",
      type: MediaType.Video,
      file: "/api/placeholder/400/300",
      createdAt: "2024-01-10",
      size: "15.7 MB",
    },
    {
      id: 3,
      title: "Company Event Photos",
      type: MediaType.Image,
      file: "/api/placeholder/400/300",
      createdAt: "2024-01-05",
      size: "5.2 MB",
    },
    {
      id: 4,
      title: "Tutorial Series",
      type: MediaType.Video,
      file: "/api/placeholder/400/300",
      createdAt: "2024-01-01",
      size: "28.3 MB",
    },
  ]);
  const [flag, setFlag] = useState(false);
  const [file, setFile] = useState(null);

  // Handle Add/Edit Form Submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isEditing && editingMedia) {
      // Update existing media
      const formDataRequest = new FormData();
      formDataRequest.append("media_id", editingMedia.id);
      formDataRequest.append("title", formData.title);
      formDataRequest.append("type", formData.type);
      if (file) {
        formDataRequest.append("file", file);
        formDataRequest.append(
          "size",
          file ? `${(file.size / 1024 / 1024).toFixed(1)} MB` : "0 MB"
        );
      }

      await axios.patch(
        process.env.REACT_APP_BASE_URL + "media/update-media",
        formDataRequest
      );
      setFlag(!flag);
    } else {
      // Add new media
      const formDataRequest = new FormData();
      formDataRequest.append("title", formData.title);
      formDataRequest.append("type", formData.type);
      formDataRequest.append("file", formData.file);
      formDataRequest.append(
        "size",
        formData.file
          ? `${(formData.file.size / 1024 / 1024).toFixed(1)} MB`
          : "0 MB"
      );
      await axios.post(
        process.env.REACT_APP_BASE_URL + "media/add-media",
        formDataRequest
      );
      setFlag(!flag);
    }
    resetForm();
  };

  // Handle Edit
  const handleEdit = (media) => {
    setFormData({
      title: media.title,
      type: media.type,
      file: null,
    });
    setFilePreview(media.file);
    setEditingMedia(media);
    setIsEditing(true);
    setIsAdding(true);
  };

  // Handle Delete
  const handleDelete = async (mediaId) => {
    // setMediaItems(mediaItems.filter((m) => m.id !== mediaId));
    await axios.delete(process.env.REACT_APP_BASE_URL + "media/delete-media", {
      params: {
        media_id: mediaId,
      },
    });
    setFlag(!flag);
  };

  // Reset form and states
  const resetForm = () => {
    setFormData({ title: "", type: MediaType.Image, file: null });
    setFilePreview(null);
    setIsAdding(false);
    setIsEditing(false);
    setEditingMedia(null);
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "file" && files.length > 0) {
      const file = files[0];
      if (isEditing) {
        setFile(file);
      } else {
        setFormData({
          ...formData,
          file: file,
        });
      }

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setFilePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Get media type icon
  const getMediaIcon = (type) => {
    return type === MediaType.Image ? "🖼️" : "🎥";
  };

  // Get media type color
  const getMediaColor = (type) => {
    return type === MediaType.Image
      ? "from-green-500 to-green-600"
      : "from-green-600 to-green-700";
  };

  // Get media type badge color
  const getMediaBadgeColor = (type) => {
    return type === MediaType.Image
      ? "bg-green-100 text-green-800"
      : "bg-green-200 text-green-900";
  };

  useEffect(() => {
    axios
      .get(process.env.REACT_APP_BASE_URL + "media/get-all-media")
      .then((res) => setMediaItems(res.data.data));
  }, [flag]);

  return (
    <div className="space-y-8">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-2xl shadow-lg p-6 transform hover:scale-105 transition-all duration-300 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Total Media</p>
              <p className="text-3xl font-bold text-gray-800 mt-2">
                {mediaItems.length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-green-600 text-xl">📁</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 transform hover:scale-105 transition-all duration-300 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Images</p>
              <p className="text-3xl font-bold text-gray-800 mt-2">
                {
                  mediaItems.filter((item) => item.type === MediaType.Image)
                    .length
                }
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-green-600 text-xl">🖼️</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 transform hover:scale-105 transition-all duration-300 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Videos</p>
              <p className="text-3xl font-bold text-gray-800 mt-2">
                {
                  mediaItems.filter((item) => item.type === MediaType.Video)
                    .length
                }
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-green-600 text-xl">🎥</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 transform hover:scale-105 transition-all duration-300 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Total Size</p>
              <p className="text-lg font-bold text-gray-800 mt-2">
                {mediaItems
                  .reduce((total, item) => {
                    const size = parseFloat(item.size);
                    return total + size;
                  }, 0)
                  .toFixed(1)}{" "}
                MB
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-green-600 text-xl">💾</span>
            </div>
          </div>
        </div>
      </div>

      {/* Header with Add Button */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Media</h2>
          <p className="text-gray-600 mt-1">
            Manage your images and videos in one place
          </p>
        </div>
        <button
          onClick={() => setIsAdding(true)}
          className="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center space-x-2 group"
        >
          <span className="group-hover:scale-110 transition-transform duration-200">
            +
          </span>
          <span>Add Media</span>
        </button>
      </div>

      {/* Add/Edit Media Form Modal */}
      {isAdding && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl transform animate-scale-in max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-green-200 bg-gradient-to-r from-green-50 to-green-100 rounded-t-2xl">
              <h3 className="text-xl font-bold text-gray-800">
                {isEditing ? "Edit Media" : "Add New Media"}
              </h3>
              <p className="text-green-600 text-sm mt-1">
                {isEditing
                  ? "Update the media details"
                  : "Upload new image or video to your library"}
              </p>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Media File *
                </label>
                <div className="flex items-center space-x-6">
                  <div className="w-40 h-32 bg-green-100 rounded-lg border-2 border-dashed border-green-300 flex items-center justify-center overflow-hidden">
                    {filePreview ? (
                      formData.type === MediaType.Image ? (
                        <img
                          src={filePreview}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-green-200 flex items-center justify-center">
                          <span className="text-4xl text-green-600">🎥</span>
                        </div>
                      )
                    ) : (
                      <span className="text-green-400 text-3xl">
                        {formData.type === MediaType.Image ? "🖼️" : "🎥"}
                      </span>
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      name="file"
                      accept={
                        formData.type === MediaType.Image
                          ? "image/*"
                          : "video/*"
                      }
                      onChange={handleChange}
                      required={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                    />
                    <p className="text-sm text-gray-500 mt-2">
                      {formData.type === MediaType.Image
                        ? "Upload an image file (JPEG, PNG, WebP)"
                        : "Upload a video file (MP4, WebM, MOV)"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Title Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Media Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                  placeholder="Enter media title"
                />
              </div>

              {/* Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Media Type *
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <label
                    className={`relative flex cursor-pointer rounded-xl border p-4 focus:outline-none ${
                      formData.type === MediaType.Image
                        ? "border-green-500 bg-green-50 ring-2 ring-green-500"
                        : "border-gray-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name="type"
                      value={MediaType.Image}
                      checked={formData.type === MediaType.Image}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <div className="flex w-full items-center justify-between">
                      <div className="flex items-center">
                        <div className="text-sm">
                          <span className="font-medium text-gray-900">
                            Image
                          </span>
                          <p className="text-gray-500">JPEG, PNG, WebP</p>
                        </div>
                      </div>
                      <span className="text-2xl">🖼️</span>
                    </div>
                  </label>

                  <label
                    className={`relative flex cursor-pointer rounded-xl border p-4 focus:outline-none ${
                      formData.type === MediaType.Video
                        ? "border-green-500 bg-green-50 ring-2 ring-green-500"
                        : "border-gray-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name="type"
                      value={MediaType.Video}
                      checked={formData.type === MediaType.Video}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <div className="flex w-full items-center justify-between">
                      <div className="flex items-center">
                        <div className="text-sm">
                          <span className="font-medium text-gray-900">
                            Video
                          </span>
                          <p className="text-gray-500">MP4, WebM, MOV</p>
                        </div>
                      </div>
                      <span className="text-2xl">🎥</span>
                    </div>
                  </label>
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-3 rounded-xl font-semibold hover:from-green-600 hover:to-green-700 transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  {isEditing ? "Update Media" : "Add Media"}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-400 transition-colors duration-200"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Media Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {mediaItems.map((media, index) => (
          <div
            key={media.id}
            className="bg-white rounded-2xl shadow-lg overflow-hidden transform hover:scale-105 transition-all duration-300 group animate-fade-in border border-green-100"
            style={{ animationDelay: `${index * 100}ms` }}
          >
            {/* Media Preview */}
            <div
              className={`h-48 bg-gradient-to-br ${getMediaColor(
                media.type
              )} relative overflow-hidden`}
            >
              <div className="w-full h-full flex items-center justify-center">
                <span className="text-6xl text-white opacity-80">
                  {getMediaIcon(media.type)}
                </span>
              </div>
              <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-1">
                <button
                  onClick={() => handleEdit(media)}
                  className="w-8 h-8 bg-white text-green-600 rounded-full flex items-center justify-center hover:bg-green-50 transition-colors duration-200 transform hover:scale-110 shadow-sm"
                  title="Edit Media"
                >
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => handleDelete(media.id)}
                  className="w-8 h-8 bg-white text-red-600 rounded-full flex items-center justify-center hover:bg-red-50 transition-colors duration-200 transform hover:scale-110 shadow-sm"
                  title="Delete Media"
                >
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </div>
              <div className="absolute top-3 left-3">
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${getMediaBadgeColor(
                    media.type
                  )}`}
                >
                  {media.type}
                </span>
              </div>
            </div>

            {/* Media Details */}
            <div className="p-4">
              <h3 className="font-semibold text-gray-800 mb-2 group-hover:text-green-700 transition-colors duration-200 line-clamp-2">
                {media.title}
              </h3>

              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center justify-between">
                  <span className="flex items-center space-x-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    <span>{media.type}</span>
                  </span>
                  <span className="text-green-600 font-medium">
                    {media.size}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                  <span>Added {formatDate(media.createdAt)}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-2 mt-4">
                <button className="flex-1 bg-green-50 text-green-600 py-2 px-3 rounded-lg font-medium hover:bg-green-100 transition-colors duration-200 text-sm">
                  Preview
                </button>
                <button className="flex-1 bg-blue-50 text-blue-600 py-2 px-3 rounded-lg font-medium hover:bg-blue-100 transition-colors duration-200 text-sm">
                  Download
                </button>
              </div>
            </div>
          </div>
        ))}

        {/* Add New Media Card */}
        <div
          className="border-2 border-dashed border-green-300 rounded-2xl p-8 text-center hover:border-green-500 hover:bg-green-50 transition-all duration-300 cursor-pointer group min-h-[300px] flex items-center justify-center"
          onClick={() => setIsAdding(true)}
        >
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-green-200 transition-colors duration-200">
            <span className="text-2xl text-green-600 group-hover:scale-110 transition-transform duration-200">
              +
            </span>
          </div>
          <h3 className="text-lg font-semibold text-gray-700 mb-2 group-hover:text-green-700 transition-colors duration-200">
            Add New Media
          </h3>
          <p className="text-gray-500 text-sm group-hover:text-gray-600 transition-colors duration-200">
            Upload images or videos to your media library
          </p>
        </div>
      </div>

      {/* Empty State */}
      {mediaItems.length === 0 && (
        <div className="text-center py-16">
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-4xl text-green-600">📁</span>
          </div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">
            No media files yet
          </h3>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            Start building your media library by uploading images and videos.
            Organize them for easy access across your platform.
          </p>
          <button
            onClick={() => setIsAdding(true)}
            className="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-green-600 hover:to-green-700 transition-all duration-200 transform hover:scale-105 shadow-lg"
          >
            Upload First Media
          </button>
        </div>
      )}

      {/* Quick Stats Footer */}
      {mediaItems.length > 0 && (
        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-2xl p-6 text-white">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-sm opacity-90">Total Files</p>
              <p className="text-2xl font-bold">{mediaItems.length}</p>
            </div>
            <div>
              <p className="text-sm opacity-90">Images</p>
              <p className="text-2xl font-bold">
                {
                  mediaItems.filter((item) => item.type === MediaType.Image)
                    .length
                }
              </p>
            </div>
            <div>
              <p className="text-sm opacity-90">Videos</p>
              <p className="text-2xl font-bold">
                {
                  mediaItems.filter((item) => item.type === MediaType.Video)
                    .length
                }
              </p>
            </div>
            <div>
              <p className="text-sm opacity-90">Total Size</p>
              <p className="text-2xl font-bold">
                {mediaItems
                  .reduce((total, item) => {
                    const size = parseFloat(item.size);
                    return total + size;
                  }, 0)
                  .toFixed(1)}{" "}
                MB
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Media;
